import React from 'react';
import {
  Box,
  Grid,
  styled,
  Typography
} from '@mui/material';
import {
  FormProvider,
  useForm
} from 'react-hook-form';
import ActionButton from "controls/global/action-button";
import {
  colors,
  fontWeight,
  gaps,
  padding
} from 'theme/defaultStyle';
import SharingAgencyGrid from "./SharingAgencyGrid"
import { isCompanyLocationType } from 'pages/assign/new/InventoryAssignNewHelper';
import newShareValidationSchema from './newShareValidationSchema';
import { getNextSharingAgencyItem } from './SharingAgencyHelper';
import AgencyLocationField from "../../components/AgencyLocationField";
import { axiosSecuredInstance } from "configurations/axiosConfig";
import { yupResolver } from "@hookform/resolvers/yup";
import { CompanyLocation } from "entities/UIModel/CompanyLocation";
import { InventoryAssignForm } from "entities/UIModel/InventoryAssignForm";
import { useAutomaticProgressDialogActions } from "utils/context/AutomaticProgressDialogContext";
import { hasValue } from 'utils/shared';
import FlashMessage from 'controls/global/flash-message/FlashMessage';
import {
  FlashMessageType,
  UIConstants
} from 'utils/data/enum';
import { useFlashMessageActions } from 'utils/context/FlashMessageContext';
import useFlashMessage from 'utils/custom-hooks/useFlashMessage';

const SHARE_SUCCESS_MESSAGE_HEADER = "Shared Inventory has been successfully setup";
const SHARE_ERROR_MESSAGE_HEADER = "Share Inventory Failed!";
const SHARE_ERROR_MESSAGE_DETAIL = "Please try again. ";

const ContainerBox = styled(Box)({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  gap: gaps.large1,
  "& .gridStyle": {
    paddingBottom: "0 !important" as any,
  },
});

const SharingAgencyBox = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.large1,
});

const SubmitBox = styled("div")({
  paddingTop: padding.xlarge1,
});


const initialValues: InventoryAssignForm = {
  locationFullDisplayName: "",
  selectedLocationFullDisplayName: "",
  notes: "",
  sharingAgencyItems: [getNextSharingAgencyItem([])],
  companyID: "",
  companyName: "",
  companyLegacyID: "",
  locationUniqueID: "",
  locationLegacyID: "",
  locationDisplayName: "",
  locationStateCode: "",
  locationStateAbbr: "",
  maxAssignCount: 0,
  maxAssignCountMessage: "",
  isActive: 1,
};

const InventorySharedNew = () => {
  const methods = useForm({
    resolver: yupResolver(newShareValidationSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: initialValues,
  });
  const { reset, getValues, clearErrors, setValue, watch } = methods;

  const sharingAgencyItems = watch("sharingAgencyItems" as const);
  // Agencies already chosen as Sharing Agencies must not appear in the
  // Assigned Agency search.
  const sharingSelectedIds = (sharingAgencyItems ?? [])
    .map((item) => item?.locationUniqueID)
    .filter((id): id is string => Boolean(id));

  const [, { openAutomaticProgressDialog, closeAutomaticProgressDialog }] =
    useAutomaticProgressDialogActions();

  // FlashMessage
  const [, { showFlashMessage, closeFlashMessage }] = useFlashMessageActions();
  useFlashMessage();

  const assignedAgencyVerified = hasValue(watch("locationUniqueID" as const));
  const allSharingAgenciesVerified =
    (sharingAgencyItems ?? []).length > 0 &&
    (sharingAgencyItems ?? []).every((item) => hasValue(item?.locationUniqueID));
  const submitDisabled = !assignedAgencyVerified || !allSharingAgenciesVerified;

  const clearAgencyLocation = () => {
    setValue("selectedLocationFullDisplayName", "");
    setValue("companyID", "");
    setValue("companyName", "");
    setValue("companyLegacyID", "");
    setValue("locationUniqueID", "");
    setValue("locationLegacyID", "");
    setValue("locationDisplayName", "");
    setValue("locationFullDisplayName", "");
    setValue("locationStateCode", "");
    setValue("locationStateAbbr", "");
    setValue("isActive", 1);
  };

  const handleLocationChange = (location: CompanyLocation | string) => {
    clearErrors(`notes`);
    if (!location || typeof location === "string") {
      clearAgencyLocation();
      setValue("locationFullDisplayName", location);
      return;
    }

    if (location && isCompanyLocationType(location)) {
      clearErrors(`locationFullDisplayName`);
      setValue("selectedLocationFullDisplayName", location?.locationFullDisplayNameWithStatus);
      setValue("companyID", location?.companyID);
      setValue("companyName", location?.companyName);
      setValue("companyLegacyID", location?.companyLegacyID);
      setValue("locationUniqueID", location?.locationUniqueID);
      setValue("locationLegacyID", location?.locationLegacyID);
      setValue("locationDisplayName", location?.locationDisplayName);
      setValue("locationFullDisplayName", location?.locationFullDisplayNameWithStatus);
      setValue("locationStateCode", location?.locationStateCode);
      setValue("locationStateAbbr", location?.locationStateAbbr);
      setValue("isActive", hasValue(location?.isActive) ? Number(location?.isActive) : 1 );
    }
  };

  const handleLocationBlur = (event: any) => {
    const newInputValue = event?.target?.value;
    const locationUniqueID = getValues("locationUniqueID");
    const selectedLocationFullDisplayName = getValues("selectedLocationFullDisplayName");
    if (
      !locationUniqueID ||
      !newInputValue ||
      !selectedLocationFullDisplayName ||
      newInputValue?.toLowerCase() !== selectedLocationFullDisplayName?.toLowerCase()
    ) {
      clearAgencyLocation();
    }
  };

  const onSubmitHandler = async (): Promise<void> => {
    const formValues = getValues();

    // Require the Assigned Agency and Re-validate at submit time.
    const sharingItems = formValues?.sharingAgencyItems ?? [];
    const allSharingVerified =
      sharingItems.length > 0 &&
      sharingItems.every((item) => hasValue(item?.locationUniqueID));

    if (!hasValue(formValues?.locationUniqueID) || !allSharingVerified) {
      return;
    }

    // The Assigned Agency (whose inventory is shared) repeats on every detail
    // row via the Assigned* fields of InventoryShareDetail.
    const assignedAgency = {
      AssignedCompanyID: formValues?.companyID,
      AssignedCompanyName: formValues?.companyName,
      AssignedCompanyLegacyID: formValues?.companyLegacyID,
      AssignedLocationUniqueID: formValues?.locationUniqueID,
      AssignedLocationLegacyID: formValues?.locationLegacyID,
      AssignedLocationDisplayName: formValues?.locationDisplayName,
      AssignedLocationStateCode: formValues?.locationStateCode,
      AssignedLocationStateAbbr: formValues?.locationStateAbbr,
    };

    // Each Sharing Agency (the agency the inventory is shared WITH) becomes one
    // InventoryShareDetail row, pairing the Assigned Agency with the Shared* fields.
    const inventoryShareDetails = sharingItems.map((item) => ({
      ...assignedAgency,
      SharedCompanyID: item?.companyID,
      SharedCompanyName: item?.companyName,
      SharedCompanyLegacyID: item?.companyLegacyID,
      SharedLocationUniqueID: item?.locationUniqueID,
      SharedLocationLegacyID: item?.locationLegacyID,
      SharedLocationDisplayName: item?.locationDisplayName,
      SharedLocationStateCode: item?.locationStateCode,
      SharedLocationStateAbbr: item?.locationStateAbbr,
    }));

    // server-side from the auth context.
    const payload = {
      inventoryShareDetails,
    };

    try {
      openAutomaticProgressDialog("Please wait while saving your shared inventory");
      await axiosSecuredInstance.post("/InventoryShare/save", payload);
      closeAutomaticProgressDialog();
      showFlashMessage(FlashMessageType.Success, SHARE_SUCCESS_MESSAGE_HEADER, "");
      reset(initialValues);
    } catch (error: any) {
      closeAutomaticProgressDialog();
      // Surface the real failure so we can tell client vs. backend issues.
      // eslint-disable-next-line no-console
      console.error("Share Inventory POST failed", {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
        payload,
      });
      showFlashMessage(FlashMessageType.Error, SHARE_ERROR_MESSAGE_HEADER, SHARE_ERROR_MESSAGE_DETAIL);
    }
  }

  function handleFlashMessageClose(
    event: React.SyntheticEvent | Event,
    reason?: string
  ) {
    if (reason === "clickaway") {
      return;
    }

    closeFlashMessage();
  }

  return (
    <FormProvider {...methods}>
      <ContainerBox>
        <Grid item xs={12} md={6} className={"gridStyle"}>
          <Typography sx ={{ color: colors.blue15, fontWeight: fontWeight.bold2, mb:2}}>
              Assigned Agency (the agency whose inventory is shared)
          </Typography>
          <AgencyLocationField
            label="Assigned Agency/Legacy ID (Location)"
            name="locationFullDisplayName"
            onChange={handleLocationChange}
            onBlur={handleLocationBlur}
            onClear={clearAgencyLocation}
            excludeIds={sharingSelectedIds}
          />

          <SharingAgencyBox>
              <SharingAgencyGrid />
          </SharingAgencyBox>
          <Typography sx ={{ mt:2}}>
              Tip: Typing &quot;ALL&quot; in the Sharing Agency field will pull back &quot;All Agencies&quot; records.
          </Typography>
        </Grid>
      </ContainerBox>
      <SubmitBox>
        <ActionButton
          variant="outlined"
          color="primary"
          onClick={onSubmitHandler}
          disabled={submitDisabled}
          >
          Submit
        </ActionButton>
      </SubmitBox>
      <FlashMessage
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        autoHideDuration={Number(
          UIConstants.FLASH_MESSAGE_AUTO_HIDDEN_DURATION
        )}
        onClose={handleFlashMessageClose}
      />
    </FormProvider>
  );
}

export default InventorySharedNew
